// frontend/src/lib/auditHistory.js
// Audit snapshot history utilities (localStorage-backed)
//
// Goals:
// - Store immutable assessment snapshots ("payloads")
// - Maintain a fast index list for Reports (filtered by active business)
// - Back-compat: AuditAssessment.jsx may still call appendAuditSnapshot()
// - Repair: rebuild index by scanning payload keys if needed
//
// IMPORTANT: This file is intentionally defensive.
// If older versions stored snapshots under different keys, rebuild should still find them.

import { readFxCache } from "./fxRates";

// -------------------------
// Storage keys (canonical)
// -------------------------
export const ACTIVE_BIZ_KEY = "aifine_active_business_id_v1";
export const SNAPSHOT_INDEX_KEY = "aifine_audit_snapshot_index_v1";
export const SNAPSHOT_PAYLOAD_PREFIX = "aifine_audit_snapshot_payload_snap_";

// Remediation context (snapshot-bound)
export const REMEDIATION_CONTEXT_KEY = "aifine_remediation_context_v1";

// -------------------------
// Legacy/back-compat keys
// -------------------------
// Older builds may have used different index keys or payload prefixes.
const LEGACY_INDEX_KEYS = [
  "aifine_audit_snapshot_index", // older without _v1
  "aifine_audit_snapshot_index_v0",
  "aifine_snapshot_index",
];

// Payload prefixes we will scan during rebuild.
// Keep the canonical prefix FIRST.
const LEGACY_PAYLOAD_PREFIXES = [
  SNAPSHOT_PAYLOAD_PREFIX,
  "aifine_audit_snapshot_payload_", // common older pattern (no snap_)
  "aifine_audit_snapshot_payload_v1_", // possible variant
  "aifine_snapshot_payload_",
];

// Older flows sometimes stored only public results, not immutable snapshots
const PUBLIC_AUDIT_RESULTS_KEY = "aifine_public_audit_results";
const PUBLIC_AUDIT_RESULTS_BIZ_PREFIX = "aifine_public_audit_results_business_";

// -------------------------
// Small helpers
// -------------------------
function nowIso() {
  try {
    return new Date().toISOString();
  } catch {
    return "—";
  }
}

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function safeStringify(x) {
  try {
    return JSON.stringify(x);
  } catch {
    return null;
  }
}

function readLS(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLS(key, val) {
  try {
    localStorage.setItem(key, val);
    return true;
  } catch {
    return false;
  }
}

function normalizeArray(x) {
  if (Array.isArray(x)) return x;
  return [];
}

function asString(x) {
  const s = String(x ?? "");
  return s.trim();
}

function uniqBySnapshotId(rows) {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const id = asString(r?.snapshot_id);
    if (!id) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(r);
  }
  return out;
}

function sortNewestFirst(rows) {
  return (rows || []).slice().sort((a, b) => {
    const ta = Date.parse(a?.created_at_iso || "") || 0;
    const tb = Date.parse(b?.created_at_iso || "") || 0;
    return tb - ta;
  });
}

function isObject(x) {
  return x && typeof x === "object" && !Array.isArray(x);
}

function pickFirstIndexKeyThatExists() {
  // Prefer canonical key if it exists; else fall back to legacy keys.
  const rawCanon = readLS(SNAPSHOT_INDEX_KEY);
  if (rawCanon) return SNAPSHOT_INDEX_KEY;

  for (const k of LEGACY_INDEX_KEYS) {
    const raw = readLS(k);
    if (raw) return k;
  }
  // Default to canonical even if empty
  return SNAPSHOT_INDEX_KEY;
}

function readIndexAllUnfiltered() {
  const idxKey = pickFirstIndexKeyThatExists();
  const raw = readLS(idxKey);
  const arr = normalizeArray(safeParse(raw));
  return sortNewestFirst(arr);
}

function writeIndexAll(rows) {
  const raw = safeStringify(rows || []);
  if (!raw) return false;
  // Always write to canonical key going forward.
  const ok = writeLS(SNAPSHOT_INDEX_KEY, raw);

  // Optional cleanup: if there was an older index key, leave it alone (do not delete) to avoid surprises.
  return ok;
}

// -------------------------
// Active business
// -------------------------
export function getActiveBusinessId() {
  const v = readLS(ACTIVE_BIZ_KEY);
  return v ? asString(v) : "";
}

export function getActiveBusiness() {
  const business_id = getActiveBusinessId();
  return {
    business_id,
    id: business_id,
    exists: Boolean(business_id),
  };
}

export function getSnapshotIndexRowById(snapshotId) {
  const sid = asString(snapshotId);
  if (!sid) return null;

  const rows = readIndexAllUnfiltered();
  return rows.find((row) => asString(row?.snapshot_id) === sid) || null;
}

function deriveBusinessIdFromSnapshot({ snapshotId, row, payload } = {}) {
  const sid = asString(snapshotId);
  const r = isObject(row) ? row : {};
  const p = isObject(payload) ? payload : {};

  return (
    asString(r?.business_id) ||
    asString(p?.business_id) ||
    asString(p?.config?.business_id) ||
    asString(p?.meta?.business_id) ||
    asString(p?.biz_id) ||
    asString(p?.meta?.biz_id) ||
    (sid ? asString(getSnapshotIndexRowById(sid)?.business_id) : "")
  );
}

function snapshotBelongsToBusiness({ snapshotId, businessId } = {}) {
  const sid = asString(snapshotId);
  const bid = asString(businessId);
  if (!sid || !bid) return false;

  const row = getSnapshotIndexRowById(sid);
  const payload = getAuditSnapshotPayload(sid);
  const ownerBusinessId = deriveBusinessIdFromSnapshot({ snapshotId: sid, row, payload });
  return ownerBusinessId === bid;
}

export function getLatestSnapshotForBusiness({ businessId } = {}) {
  const bid = asString(businessId) || getActiveBusinessId();
  if (!bid) return null;

  const rows = readAuditSnapshotIndex({ businessId: bid });
  const row = Array.isArray(rows) && rows.length ? rows[0] : null;
  if (!row?.snapshot_id) return null;

  const snapshot_id = asString(row.snapshot_id);
  const payload = getAuditSnapshotPayload(snapshot_id);

  return {
    snapshot_id,
    business_id: deriveBusinessIdFromSnapshot({ snapshotId: snapshot_id, row, payload }) || bid,
    row,
    payload: isObject(payload) ? payload : null,
  };
}

export function getCanonicalRemediationContext({ businessId, preferredSnapshotId } = {}) {
  const bid = asString(businessId) || getActiveBusinessId();
  const preferredId = asString(preferredSnapshotId);

  if (preferredId && snapshotBelongsToBusiness({ snapshotId: preferredId, businessId: bid })) {
    const row = getSnapshotIndexRowById(preferredId);
    const payload = getAuditSnapshotPayload(preferredId);
    return buildRemediationContext({
      snapshotId: preferredId,
      payload,
      meta: {
        ...(isObject(row) ? row : {}),
        business_id: deriveBusinessIdFromSnapshot({ snapshotId: preferredId, row, payload }) || bid,
      },
    });
  }

  const latest = getLatestSnapshotForBusiness({ businessId: bid });
  if (latest?.snapshot_id) {
    return buildRemediationContext({
      snapshotId: latest.snapshot_id,
      payload: latest.payload,
      meta: {
        ...(isObject(latest.row) ? latest.row : {}),
        business_id: latest.business_id || bid,
      },
    });
  }

  return buildRemediationContext({
    snapshotId: "",
    payload: null,
    meta: { business_id: bid },
  });
}

// -------------------------
// FX embedding (audit-date FX in snapshot)
// -------------------------

function safeUpper(s) {
  return String(s || "").trim().toUpperCase();
}

/**
 * Detect multi-currency from v3 risk_exposure.combined_by_currency.
 * Returns an array of currency codes (uppercased).
 */
function extractCurrencyCodesFromPayload(payload) {
  if (!isObject(payload)) return [];

  const rx = payload?.risk_exposure;
  const by = rx?.combined_by_currency;
  if (!isObject(by)) return [];

  return Object.keys(by)
    .map(safeUpper)
    .filter(Boolean);
}

/**
 * Determine whether a payload already contains a usable FX cache.
 * We accept a few shapes to be robust.
 */
function payloadHasFxCache(payload) {
  if (!isObject(payload)) return false;

  const candidates = [
    payload?.fx_cache,
    payload?.fxCache,
    payload?.meta?.fx_cache,
    payload?.meta?.fxCache,
    payload?.summary?.fx_cache,
    payload?.summary?.fxCache,
    payload?.risk_exposure?.fx_cache,
    payload?.risk_exposure?.fxCache,
    payload?.risk_exposure?.fx,
  ];

  for (const c of candidates) {
    if (!isObject(c)) continue;
    const base = safeUpper(c?.base || "");
    const rates = c?.rates;
    if (base && isObject(rates) && Object.keys(rates).length) return true;
  }

  return false;
}

/**
 * Enrich a snapshot payload with an "audit-date FX cache" when:
 * - multi-currency snapshot, AND
 * - payload does not already have fx cache, AND
 * - localStorage fx cache is present and well-formed
 *
 * IMPORTANT:
 * - This runs ONLY at snapshot creation time.
 * - Does not mutate existing stored snapshots.
 */
function maybeEmbedAuditFxCache(payload) {
  if (!isObject(payload)) return payload;

  const codes = extractCurrencyCodesFromPayload(payload);
  const isMulti = codes.length > 1;

  if (!isMulti) return payload;
  if (payloadHasFxCache(payload)) return payload;

  const fx = readFxCache();
  if (!isObject(fx)) return payload;

  const base = safeUpper(fx?.base || "");
  const rates = fx?.rates;

  if (!base || !isObject(rates) || !Object.keys(rates).length) return payload;

  const fxCache = {
    version: fx?.version || "fx.snapshot",
    source_label: fx?.source_label || "Frankfurter (ECB reference rates)",
    retrieved_at_iso: fx?.retrieved_at_iso || null,
    as_of_iso: fx?.as_of_iso || null,
    base,
    rates,
  };

  // Canonical location + meta mirror for traceability.
  const nextMeta = isObject(payload.meta)
    ? { ...payload.meta, fx_cache: fxCache }
    : { fx_cache: fxCache };

  return { ...payload, fx_cache: fxCache, meta: nextMeta };
}

// -------------------------
// Snapshot payload storage
// -------------------------
export function storeAuditSnapshotPayload(snapshotId, payload = {}) {
  const id = asString(snapshotId) || `${Date.now()}`;
  const key = `${SNAPSHOT_PAYLOAD_PREFIX}${id}`;

  // Ensure payload has snapshot_id for traceability (CANONICAL ENFORCEMENT)
  const base =
    payload && typeof payload === "object"
      ? { ...payload, snapshot_id: id }
      : { snapshot_id: id, payload };

  // Embed audit-date FX cache for multi-currency snapshots (creation-time only)
  const enriched = maybeEmbedAuditFxCache(base);

  const raw = safeStringify(enriched);
  if (!raw) throw new Error("Unable to serialize snapshot payload.");

  const ok = writeLS(key, raw);
  if (!ok) throw new Error("Unable to store snapshot payload (storage full or blocked).");

  return id;
}

function tryReadPayloadByAnyPrefix(snapshotId) {
  const id = asString(snapshotId);
  if (!id) return null;

  // Try canonical first
  const rawCanon = readLS(`${SNAPSHOT_PAYLOAD_PREFIX}${id}`);
  const pCanon = safeParse(rawCanon);
  if (isObject(pCanon)) return pCanon;

  // Try legacy prefixes
  for (const pref of LEGACY_PAYLOAD_PREFIXES) {
    if (pref === SNAPSHOT_PAYLOAD_PREFIX) continue;
    const raw = readLS(`${pref}${id}`);
    const p = safeParse(raw);
    if (isObject(p)) return p;
  }
  return null;
}

export function getAuditSnapshotPayload(snapshotId) {
  return tryReadPayloadByAnyPrefix(snapshotId);
}

// -------------------------
// Snapshot index (for Reports list)
// -------------------------
export function readAuditSnapshotIndex({ businessId } = {}) {
  const active = asString(businessId) || getActiveBusinessId();
  const arr = readIndexAllUnfiltered();

  // If there is no active business, return everything (useful for debugging)
  if (!active) return sortNewestFirst(arr);

  // Filter to active business only
  const filtered = arr.filter((r) => asString(r?.business_id) === active);
  return sortNewestFirst(filtered);
}

export function upsertAuditSnapshotIndexRow(row = {}) {
  const snapshot_id = asString(row?.snapshot_id);
  if (!snapshot_id) throw new Error("Index row missing snapshot_id.");

  const arr = readIndexAllUnfiltered();

  const nextRow = {
    snapshot_id,
    created_at_iso: row?.created_at_iso || nowIso(),
    entity_name: row?.entity_name || "Not specified",
    scope: Array.isArray(row?.scope) ? row.scope : [],
    compliance_score_pct:
      typeof row?.compliance_score_pct === "number"
        ? row.compliance_score_pct
        : row?.compliance_score_pct ?? null,
    failed_count: Number.isFinite(Number(row?.failed_count)) ? Number(row.failed_count) : 0,
    unsure_count: Number.isFinite(Number(row?.unsure_count)) ? Number(row.unsure_count) : 0,
    total_questions: Number.isFinite(Number(row?.total_questions)) ? Number(row.total_questions) : 60,
    business_id: asString(row?.business_id) || getActiveBusinessId(),
  };

  // Replace if exists
  const replaced = arr.map((r) => (asString(r?.snapshot_id) === snapshot_id ? nextRow : r));
  const exists = arr.some((r) => asString(r?.snapshot_id) === snapshot_id);
  const merged = exists ? replaced : [nextRow, ...arr];

  const uniq = uniqBySnapshotId(merged);
  const ok = writeIndexAll(uniq);
  if (!ok) throw new Error("Unable to update snapshot index.");

  return nextRow;
}

// -------------------------
// Back-compat: appendAuditSnapshot(payload)
// -------------------------
export function appendAuditSnapshot(payload = {}) {
  // Prefer an existing id if the app already generated one
  const snapshotId =
    asString(payload?.snapshot_id) ||
    asString(payload?.snapshotId) ||
    asString(payload?.id) ||
    `${Date.now()}`;

  // Store payload (canonical location) — storeAuditSnapshotPayload will embed FX if needed
  const id = storeAuditSnapshotPayload(snapshotId, payload);

  // Derive business id (critical for Reports filtering)
  const businessId =
    asString(payload?.business_id) ||
    asString(payload?.biz_id) ||
    asString(payload?.meta?.business_id) ||
    asString(payload?.meta?.biz_id) ||
    getActiveBusinessId();

  // Derive metadata for index row
  const createdAt =
    payload?.generated_at ||
    payload?.created_at_iso ||
    payload?.created_at ||
    payload?.meta?.created_at_iso ||
    nowIso();

  const entityName =
    payload?.entity_name ||
    payload?.entityName ||
    payload?.entity?.name ||
    payload?.inputs?.entity_name ||
    payload?.inputs?.entityName ||
    "Not specified";

  const scope = Array.isArray(payload?.summary?.jurisdictions)
    ? payload.summary.jurisdictions
    : Array.isArray(payload?.summary?.scope)
    ? payload.summary.scope
    : Array.isArray(payload?.scope)
    ? payload.scope
    : [];

  const a = payload?.assessment || {};
  const complianceScorePct =
    typeof a?.compliance_score_pct === "number"
      ? a.compliance_score_pct
      : Number.isFinite(Number(a?.compliance_score_pct))
      ? Number(a.compliance_score_pct)
      : null;

  const failedCount =
    typeof a?.failed_count === "number"
      ? a.failed_count
      : Array.isArray(a?.failed_question_ids)
      ? a.failed_question_ids.length
      : 0;

  const unsureCount =
    typeof a?.unsure_count === "number"
      ? a.unsure_count
      : Array.isArray(a?.unsure_question_ids)
      ? a.unsure_question_ids.length
      : 0;

  const totalQuestions =
    typeof a?.total_questions === "number"
      ? a.total_questions
      : Array.isArray(a?.active_question_ids)
      ? a.active_question_ids.length
      : 60;

  // Write/replace index row
  upsertAuditSnapshotIndexRow({
    snapshot_id: id,
    created_at_iso: createdAt,
    entity_name: entityName,
    scope,
    compliance_score_pct: complianceScorePct,
    failed_count: failedCount,
    unsure_count: unsureCount,
    total_questions: totalQuestions,
    business_id: businessId,
  });

  return id;
}

// -------------------------
// Recovery: build index rows from a payload object
// -------------------------
function buildIndexRowFromPayload(payload, snapshot_id_fallback = "") {
  if (!isObject(payload)) return null;

  const snapshot_id =
    asString(payload?.snapshot_id) ||
    asString(payload?.id) ||
    asString(snapshot_id_fallback) ||
    "";

  if (!snapshot_id) return null;

  const business_id =
    asString(payload?.business_id) ||
    asString(payload?.biz_id) ||
    asString(payload?.meta?.business_id) ||
    asString(payload?.meta?.biz_id) ||
    "";

  const created_at_iso =
    payload?.generated_at ||
    payload?.created_at_iso ||
    payload?.created_at ||
    payload?.meta?.created_at_iso ||
    nowIso();

  const entity_name =
    payload?.entity_name ||
    payload?.entityName ||
    payload?.entity?.name ||
    payload?.inputs?.entity_name ||
    payload?.inputs?.entityName ||
    "Not specified";

  const scope = Array.isArray(payload?.summary?.jurisdictions)
    ? payload.summary.jurisdictions
    : Array.isArray(payload?.summary?.scope)
    ? payload.summary.scope
    : Array.isArray(payload?.scope)
    ? payload.scope
    : [];

  const a = payload?.assessment || {};
  const compliance_score_pct =
    typeof a?.compliance_score_pct === "number"
      ? a.compliance_score_pct
      : Number.isFinite(Number(a?.compliance_score_pct))
      ? Number(a.compliance_score_pct)
      : null;

  const failed_count =
    typeof a?.failed_count === "number"
      ? a.failed_count
      : Array.isArray(a?.failed_question_ids)
      ? a.failed_question_ids.length
      : 0;

  const unsure_count =
    typeof a?.unsure_count === "number"
      ? a.unsure_count
      : Array.isArray(a?.unsure_question_ids)
      ? a.unsure_question_ids.length
      : 0;

  const total_questions =
    typeof a?.total_questions === "number"
      ? a.total_questions
      : Array.isArray(a?.active_question_ids)
      ? a.active_question_ids.length
      : 60;

  return {
    snapshot_id,
    created_at_iso,
    entity_name,
    scope,
    compliance_score_pct,
    failed_count,
    unsure_count,
    total_questions,
    business_id,
  };
}

// -------------------------
// Recovery: promote "public audit results" into a snapshot payload (if needed)
// -------------------------
function promotePublicResultsIfPresent() {
  // If a canonical snapshot index already exists, do nothing.
  const existingIndex = readIndexAllUnfiltered();
  if (Array.isArray(existingIndex) && existingIndex.length) return { promoted: false, snapshotId: "" };

  // Try global public results first
  const global = safeParse(readLS(PUBLIC_AUDIT_RESULTS_KEY));
  if (isObject(global)) {
    const sid = `${Date.now()}`;
    storeAuditSnapshotPayload(sid, global);
    const row = buildIndexRowFromPayload(global, sid);
    if (row) {
      const next = uniqBySnapshotId([row, ...existingIndex]);
      writeIndexAll(sortNewestFirst(next));
      return { promoted: true, snapshotId: sid };
    }
  }

  // Try per-business public results (scan keys)
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (!k.startsWith(PUBLIC_AUDIT_RESULTS_BIZ_PREFIX)) continue;

      const obj = safeParse(readLS(k));
      if (!isObject(obj)) continue;

      const sid = `${Date.now()}_${i}`;
      storeAuditSnapshotPayload(sid, obj);
      const row = buildIndexRowFromPayload(obj, sid);
      if (row) {
        const next = uniqBySnapshotId([row, ...existingIndex]);
        writeIndexAll(sortNewestFirst(next));
        return { promoted: true, snapshotId: sid };
      }
    }
  } catch {
    // ignore
  }

  return { promoted: false, snapshotId: "" };
}

// -------------------------
// Recovery: rebuild index by scanning payload keys
// Use if index is missing/corrupted but payloads exist.
// -------------------------
export function rebuildAuditSnapshotIndexFromPayloads() {
  const rows = [];

  // 0) If we have only legacy "public audit results", promote one into snapshot storage
  promotePublicResultsIfPresent();

  // 1) Scan all known payload prefixes
  const prefixes = Array.from(new Set(LEGACY_PAYLOAD_PREFIXES.filter(Boolean)));

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;

      const matchedPrefix = prefixes.find((p) => k.startsWith(p));
      if (!matchedPrefix) continue;

      const raw = readLS(k);
      const payload = safeParse(raw);
      if (!isObject(payload)) continue;

      const snapshotIdFromKey = asString(k.slice(matchedPrefix.length));
      const row = buildIndexRowFromPayload(payload, snapshotIdFromKey);
      if (row) rows.push(row);
    }
  } catch {
    // fall through
  }

  // 2) If index exists under a legacy key, merge it too (do not trust it fully, but it can surface ids)
  const legacyMerged = [];
  try {
    for (const legacyKey of LEGACY_INDEX_KEYS) {
      const raw = readLS(legacyKey);
      const arr = normalizeArray(safeParse(raw));
      legacyMerged.push(...arr);
    }
  } catch {
    // ignore
  }

  // If legacy index has snapshot_ids that payload scan missed, try to load payload by any prefix
  for (const r of legacyMerged) {
    const sid = asString(r?.snapshot_id);
    if (!sid) continue;
    if (rows.some((x) => asString(x?.snapshot_id) === sid)) continue;

    const p = tryReadPayloadByAnyPrefix(sid);
    const row = p ? buildIndexRowFromPayload(p, sid) : null;
    if (row) rows.push(row);
  }

  const deduped = uniqBySnapshotId(rows);
  const sorted = sortNewestFirst(deduped);

  const ok = writeIndexAll(sorted);
  if (!ok) throw new Error("Unable to rebuild snapshot index.");

  return sorted;
}

// -------------------------
// Remediation context helpers (snapshot-bound)
// -------------------------
export function readRemediationContext() {
  try {
    const raw = readLS(REMEDIATION_CONTEXT_KEY);
    const obj = safeParse(raw);
    return isObject(obj) ? obj : null;
  } catch {
    return null;
  }
}

export function buildRemediationContext({ snapshotId, payload, meta } = {}) {
  const sid = asString(snapshotId);
  const p = isObject(payload) ? payload : {};
  const m = isObject(meta) ? meta : {};

  const businessId =
    asString(p.business_id) ||
    asString(p?.config?.business_id) ||
    asString(p?.meta?.business_id) ||
    asString(m.business_id);

  const entityName =
    asString(p.entity_name) ||
    asString(p?.summary?.entity_name) ||
    asString(p?.config?.clientName) ||
    asString(m.entity_name);

  const createdAt =
    asString(p.created_at_iso) ||
    asString(p.generated_at) ||
    asString(p?.meta?.created_at_iso) ||
    asString(m.created_at_iso);

  return {
    snapshot_id: sid,
    business_id: businessId,
    entity_name: entityName,
    created_at_iso: createdAt,
    source: "auditHistory",
    written_at_iso: nowIso(),
  };
}

export function writeRemediationContext(ctx) {
  const next = isObject(ctx) ? ctx : {};
  const raw = safeStringify(next);
  if (!raw) return false;
  return writeLS(REMEDIATION_CONTEXT_KEY, raw);
}

export function setRemediationContextForSnapshot({ snapshotId, payload, meta } = {}) {
  const ctx = buildRemediationContext({ snapshotId, payload, meta });
  const ok = writeRemediationContext(ctx);
  return { ok, context: ctx };
}

// Optional default export for older import styles
export default {
  getActiveBusinessId,
  getActiveBusiness,
  getSnapshotIndexRowById,
  getLatestSnapshotForBusiness,
  getCanonicalRemediationContext,
  readAuditSnapshotIndex,
  getAuditSnapshotPayload,
  storeAuditSnapshotPayload,
  upsertAuditSnapshotIndexRow,
  appendAuditSnapshot,
  rebuildAuditSnapshotIndexFromPayloads,
  readRemediationContext,
  buildRemediationContext,
  writeRemediationContext,
  setRemediationContextForSnapshot,
};